// src/pages/Cart/Cart.jsx
// FRONT OFFICE - Cart page with Firebase integration

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaArrowLeft, FaShoppingCart } from 'react-icons/fa';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import './Cart.css';

const Cart = () => {
  const { cartItems, cartTotals, updateItemQuantity, removeItem, loading, emptyCart } = useCart();
  const { currentUser } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: '/cart',
          message: 'Please log in to view your cart' 
        } 
      });
    }
  }, [currentUser, navigate]);

  // Update quantity
  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    updateItemQuantity(itemId, newQuantity);
  };

  // Remove item completely
  const handleRemoveItem = (itemId) => {
    removeItem(itemId);
  };

  // Apply coupon
  const applyCoupon = (e) => {
    e.preventDefault();
    
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    // Simulate coupon validation (replace with actual Firebase implementation)
    if (couponCode.toUpperCase() === 'WELCOME10') {
      setAppliedCoupon({
        code: 'WELCOME10',
        discount: 10, // 10% discount
        type: 'percentage'
      });
      setCouponError('');
      setCouponCode('');
    } else {
      setCouponError('Invalid coupon code');
      setAppliedCoupon(null);
    }
  };

  // Calculate totals with coupon
  const calculateTotals = () => {
    let subtotal = cartTotals.subtotal;
    let discount = 0;
    
    if (appliedCoupon) {
      if (appliedCoupon.type === 'percentage') {
        discount = subtotal * (appliedCoupon.discount / 100);
      } else if (appliedCoupon.type === 'fixed') {
        discount = appliedCoupon.discount;
      }
    }
    
    const discountedSubtotal = subtotal - discount;
    const shippingCost = discountedSubtotal > 100 ? 0 : cartTotals.shippingCost;
    const total = discountedSubtotal + shippingCost;
    
    return {
      subtotal,
      discount,
      discountedSubtotal,
      shippingCost,
      total
    };
  };

  const totals = calculateTotals();

  // Empty cart state
  if (!loading && cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-cart">
            <h1 className="cart-title">Your Cart</h1>
            <div className="empty-cart-icon">
              <FaShoppingCart />
            </div>
            <p>Your cart is empty. Start shopping!</p>
            <Link to="/shop" className="btn btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="loading-cart">
            <div className="spinner"></div>
            <p>Loading cart...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="cart-title">Your Cart ({cartTotals.totalItems} items)</h1>

        <div className="cart-content">
          {/* Product List */}
          <div className="cart-items">
            <table className="cart-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Subtotal</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {cartItems.map((item) => (
                  <tr key={item.id} className="cart-item">
                    <td>
                      <div className="product-info">
                        <img 
                          src={item.product.image || "https://i.pinimg.com/736x/df/12/4a/df124abf279de0e5291891691e1c9bed.jpg"} 
                          alt={item.product.name} 
                          className="product-images"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://i.pinimg.com/736x/df/12/4a/df124abf279de0e5291891691e1c9bed.jpg";
                          }}
                        />
                        <div>
                          <h3>{item.product.name}</h3>
                          <p>SKU: {item.product.id}</p>
                        </div>
                      </div>
                    </td>
                    <td>€{item.product.price}</td>
                    <td>
                      <div className="quantity-controls">
                        <button 
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1 || loading}
                        >
                          −
                        </button>
                        <span>{item.quantity}</span>
                        <button 
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={loading}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td>€{(item.product.price * item.quantity)}</td>
                    <td>
                      <button 
                        onClick={() => handleRemoveItem(item.id)}
                        className="remove-btn"
                        disabled={loading}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Cart Actions */}
            <div className="cart-actions">
             
              <Link to="/shop" className="btn continue-shopping">
                <FaArrowLeft className="btn-icon" /> Continue Shopping
              </Link>
            </div>
          </div>

          {/* Order Summary */}
          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>€{totals.subtotal.toFixed(2)}</span>
            </div>
            
            {appliedCoupon && (
              <div className="summary-row discount">
                <span>Discount ({appliedCoupon.code}):</span>
                <span>-€{totals.discount.toFixed(2)}</span>
              </div>
            )}
            
            <div className="summary-row">
              <span>Shipping:</span>
              <span>{totals.shippingCost === 0 ? 'FREE' : `€${totals.shippingCost.toFixed(2)}`}</span>
            </div>
            
            <div className="summary-row total">
              <span>Total:</span>
              <span>€{totals.total.toFixed(2)}</span>
            </div>
            
            <Link to="/checkout">
              <button 
                className="checkout-btn"
                disabled={loading || cartItems.length === 0}
              >
                Proceed to Checkout
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;






















